import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { getPlanLimitError } from "@/utils/planLimits";
import { Box, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronLeft, RotateCcw } from "lucide-react";
import Joyride, { STATUS } from "react-joyride";

import { PageHeader } from "../../components/AppLayout";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { ConfirmModal } from "../../components/ConfirmModal";
import SuccessPopup from "../../components/SuccessPopup";
import { useAuth } from "../../components/contexts/AuthContext";
import { isClientReadOnly } from "../../utils/clientAccess";
import {
  REPORT_DETAIL_TUTORIAL_STEPS,
  REPORTS_TUTORIAL_MOCK_ID,
  REPORTS_TUTORIAL_MOCK_REPORT,
  ReportsTutorialTooltip,
  getReportsTutorialStyles,
} from "../../Tutorials/report/reportstutorial";
import { TUTORIAL_FLOATER_PROPS, useTutorialsEnabled } from "../../Tutorials/index.js";
import { changeReportStatus, fetchReport, reportQueryKey, updateReportText } from "../../services/reportsService";
import { REPORT_STATUS_BADGES, reportsBasePath } from "./reportFormat";
import { hasData, projectsWithActivity } from "./reportFormat";
import {
  ContentSection,
  ExecutiveSummary,
  InternalSection,
  PerformanceSection,
  PeriodDetailsSection,
  ProjectsSection,
  TrafficSection,
  VisibilitySection,
} from "./ReportSections";

const SECTION_ORDER = ["traffic", "visibility", "performance", "projects", "content", "details"];

const STATUS_ACTIONS = {
  approve: {
    title: "Aprobar informe",
    question: "¿Aprobar y enviar el informe?",
    description: "El cliente podrá verlo y recibirá una notificación. Ya no podrás editar los textos salvo que lo devuelvas a revisión.",
    confirmLabel: "Aprobar y enviar",
    successMessage: "El informe ha sido aprobado y ahora es visible para el cliente.",
  },
  revert: {
    title: "Devolver a revisión",
    question: "¿Devolver el informe a revisión?",
    description: "El cliente dejará de verlo hasta que lo vuelvas a aprobar.",
    confirmLabel: "Devolver a revisión",
    successMessage: "El informe vuelve a estar en revisión.",
  },
};

export function DetailedReport() {
  const tutorialsEnabled = useTutorialsEnabled();
  const { reportId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext, reportsRefreshKey, selectedFilterData, userRole, isMultiClientUser, changeSelectedFilter } =
    useOutletContext();
  const theme = useTheme();

  const isReportsTutorial = tutorialsEnabled && userRole !== "client" && reportId === REPORTS_TUTORIAL_MOCK_ID;
  const basePath = reportsBasePath(userRole);
  const queryKey = reportQueryKey(reportId, reportsRefreshKey);

  const [reportTutorialKey, setReportTutorialKey] = useState(0);
  const [isReportTutorialRunning, setIsReportTutorialRunning] = useState(false);
  const [reportTutorialStepIndex, setReportTutorialStepIndex] = useState(0);
  const [isTutorialTransitioning, setIsTutorialTransitioning] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [popupState, setPopupState] = useState({ isOpen: false, message: "", error: false });
  const tutorialTransitionTimeoutRef = useRef(null);

  const { data: backendData, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchReport(reportId),
    enabled: interceptorReady && !isReportsTutorial,
  });

  const report = isReportsTutorial ? REPORTS_TUTORIAL_MOCK_REPORT : backendData;

  const isInternalUser = currentUser.role !== "client";
  const canManage = !isReportsTutorial && isInternalUser && Boolean(report) && !isClientReadOnly(currentUser, report.client_id);
  const canEdit = canManage && report.status === "generated";
  const showApprove = isReportsTutorial || (canManage && report.status === "generated");
  const showRevert = canManage && report.status === "approved";

  const finishTutorial = useCallback(() => {
    navigate(basePath, { replace: true, state: { reportsTutorialCompleted: true } });
  }, [navigate, basePath]);

  const saveText = async (field, text) => {
    const data = await updateReportText(reportId, field, text);
    queryClient.setQueryData(queryKey, data);
  };

  const textSaver = (field) => (canEdit ? (text) => saveText(field, text) : undefined);

  const runStatusAction = async () => {
    setIsChangingStatus(true);
    try {
      const data = await changeReportStatus(reportId, statusAction);
      queryClient.setQueryData(queryKey, data);
      setPopupState({ isOpen: true, message: STATUS_ACTIONS[statusAction].successMessage, error: false });
    } catch (error) {
      // Lo muestra PlanFeatureRequiredDialog
      if (getPlanLimitError(error)) return;
      setPopupState({
        isOpen: true,
        message: error.response?.data?.detail || "No se pudo cambiar el estado del informe. Inténtalo de nuevo.",
        error: true,
      });
    } finally {
      setIsChangingStatus(false);
      setStatusAction(null);
    }
  };

  const goToList = useCallback(() => navigate(basePath), [navigate, basePath]);

  const clearTutorialTransitionTimeout = useCallback(() => {
    if (tutorialTransitionTimeoutRef.current) {
      window.clearTimeout(tutorialTransitionTimeoutRef.current);
      tutorialTransitionTimeoutRef.current = null;
    }
  }, []);

  const goToTutorialStep = useCallback(
    (nextStepIndex) => {
      if (isTutorialTransitioning) return;
      setIsTutorialTransitioning(true);
      clearTutorialTransitionTimeout();

      const step = REPORT_DETAIL_TUTORIAL_STEPS[nextStepIndex];
      const target = document.querySelector(step.target);
      target?.scrollIntoView({ behavior: "smooth", block: step.scrollBlock || "center", inline: "nearest" });

      tutorialTransitionTimeoutRef.current = window.setTimeout(
        () => {
          setReportTutorialStepIndex(nextStepIndex);
          setIsTutorialTransitioning(false);
          tutorialTransitionTimeoutRef.current = null;
        },
        target ? 320 : 0
      );
    },
    [clearTutorialTransitionTimeout, isTutorialTransitioning]
  );

  const tutorialSteps = useMemo(
    () =>
      REPORT_DETAIL_TUTORIAL_STEPS.map((step, index) => ({
        ...step,
        isTransitioning: isTutorialTransitioning,
        floaterProps: {
          styles: {
            arrow: {
              display: isTutorialTransitioning ? "none" : "inline-flex",
              opacity: isTutorialTransitioning ? 0 : 1,
              transition: "opacity 0.12s ease",
            },
          },
        },
        onBackClick:
          index > 0
            ? () => goToTutorialStep(index - 1)
            : () => navigate(basePath, { state: { reportsTutorialGoBack: true } }),
        // The last step finishes the tour the same way approving the mock report does.
        onPrimaryClick: index < REPORT_DETAIL_TUTORIAL_STEPS.length - 1 ? () => goToTutorialStep(index + 1) : finishTutorial,
      })),
    [finishTutorial, goToTutorialStep, isTutorialTransitioning, navigate, basePath]
  );

  useEffect(() => {
    if (!isReportsTutorial) return undefined;
    setReportTutorialStepIndex(0);
    setReportTutorialKey((current) => current + 1);
    const frameId = requestAnimationFrame(() => setIsReportTutorialRunning(true));
    return () => cancelAnimationFrame(frameId);
  }, [isReportsTutorial]);

  useEffect(() => clearTutorialTransitionTimeout, [clearTutorialTransitionTimeout]);

  useEffect(() => {
    const goToGlobalList = () => {
      if (selectedFilterData?.id !== "global") changeSelectedFilter("global");
      goToList();
    };
    const scopeLabel = { superadmin: "Agencia", agency_admin: "Cliente", consultant: "Cliente", client: "Empresa" }[userRole];
    const hasScope = selectedFilterData?.id !== "global" && Boolean(selectedFilterData?.name);
    const hasGlobalView = userRole !== "client" || isMultiClientUser;

    const breadcrumbItems = [{ label: "Vista Global", onClick: hasGlobalView ? goToGlobalList : goToList }];
    if (hasGlobalView && hasScope) {
      breadcrumbItems.push({ label: `${scopeLabel}: ${selectedFilterData.name}`, onClick: goToList });
    }
    if (report) breadcrumbItems.push({ label: `Informe: ${report.client_name} (${report.period_label})` });

    // The header is rendered inside the report column so it lines up with the content.
    setPageContext({ showTitle: false, breadcrumbItems });
  }, [changeSelectedFilter, goToList, isMultiClientUser, report, selectedFilterData, setPageContext, userRole]);

  if (!isReportsTutorial && isLoading) {
    return (
      <Stack spacing={3} sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={300} />
      </Stack>
    );
  }

  if (!report) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Typography variant="h6" color="text.secondary">
          No se pudo cargar el informe
        </Typography>
      </Box>
    );
  }

  const { analytics_data: analytics, seo_data: seo, pagespeed_data: pagespeed } = report;
  const projects = projectsWithActivity(report.projects_data);
  const visibleSections = {
    traffic: hasData(analytics),
    visibility: hasData(seo.search_console) || hasData(seo.seranking),
    performance: hasData(pagespeed),
    projects: projects.length > 0,
    content: report.content_data.total_approved > 0,
    details: Boolean(report.period_details),
  };
  const numbers = Object.fromEntries(
    SECTION_ORDER.filter((key) => visibleSections[key]).map((key, index) => [key, String(index + 1).padStart(2, "0")])
  );
  const statusBadge = REPORT_STATUS_BADGES[report.status];

  return (
    <>
      <SuccessPopup
        isOpen={popupState.isOpen}
        onClose={() => setPopupState((previous) => ({ ...previous, isOpen: false }))}
        message={popupState.message}
        error={popupState.error}
      />

      <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", pb: { xs: 1, md: 2 } }}>
        <PageHeader
          title={
            <>
              Informe:{" "}
              <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>
                {report.client_name} · {report.period_label}
              </span>
            </>
          }
          leftContent={
            <IconButton
              onClick={goToList}
              size="small"
              aria-label="Volver a informes"
              sx={{ color: "primary.main", padding: "4px", "&:hover": { backgroundColor: "action.hover" } }}
            >
              <ChevronLeft size={20} />
            </IconButton>
          }
          titleRightContent={isInternalUser ? <Badge color={statusBadge.color}>{statusBadge.label}</Badge> : null}
          rightContent={
            <>
              {showApprove && (
                <Button
                  size="sm"
                  data-tutorial="report-send"
                  onClick={() => (isReportsTutorial ? finishTutorial() : setStatusAction("approve"))}
                  sx={{ gap: 1 }}
                >
                  <CheckCircle2 size={15} />
                  Aprobar informe
                </Button>
              )}
              {showRevert && (
                <Button variant="outline" size="sm" onClick={() => setStatusAction("revert")} sx={{ gap: 1 }}>
                  <RotateCcw size={15} />
                  Revertir
                </Button>
              )}
            </>
          }
        />
        <Stack spacing={4}>
          <ExecutiveSummary report={report} numbers={numbers} onSave={textSaver("executive_summary")} />

          {numbers.traffic && (
            <TrafficSection data={analytics} number={numbers.traffic} analysis={report.traffic_analysis} onSaveAnalysis={textSaver("traffic_analysis")} />
          )}
          {numbers.visibility && (
            <VisibilitySection
              seo={seo}
              analytics={analytics}
              number={numbers.visibility}
              analysis={report.visibility_analysis}
              onSaveAnalysis={textSaver("visibility_analysis")}
            />
          )}
          {numbers.performance && (
            <PerformanceSection
              data={pagespeed}
              number={numbers.performance}
              analysis={report.performance_analysis}
              onSaveAnalysis={textSaver("performance_analysis")}
            />
          )}
          {numbers.projects && <ProjectsSection report={report} number={numbers.projects} isClient={!isInternalUser} />}
          {numbers.content && <ContentSection content={report.content_data} number={numbers.content} />}
          {numbers.details && (
            <PeriodDetailsSection report={report} number={numbers.details} onSave={textSaver("period_details")} />
          )}
          {isInternalUser && <InternalSection conversation={report.conversation_metrics} tickets={report.tickets_data} />}
        </Stack>
      </Box>

      {statusAction && (
        <ConfirmModal
          open
          onClose={() => setStatusAction(null)}
          onConfirm={runStatusAction}
          loading={isChangingStatus}
          loadingLabel="Guardando..."
          {...STATUS_ACTIONS[statusAction]}
        />
      )}

      {isReportsTutorial && (
        <Joyride
          floaterProps={TUTORIAL_FLOATER_PROPS}
          key={`report-detail-tutorial-${reportTutorialKey}`}
          steps={tutorialSteps}
          run={tutorialsEnabled && isReportTutorialRunning}
          stepIndex={reportTutorialStepIndex}
          continuous
          showSkipButton={false}
          showProgress={false}
          scrollToFirstStep
          disableOverlayClose
          disableCloseOnEsc
          disableScrolling
          disableScrollParentFix
          locale={{ close: "Cerrar", last: "Siguiente", next: "Siguiente" }}
          tooltipComponent={ReportsTutorialTooltip}
          styles={getReportsTutorialStyles(theme, 20000)}
          callback={({ status }) => {
            if (status === STATUS.SKIPPED || status === STATUS.FINISHED) {
              clearTutorialTransitionTimeout();
              setIsTutorialTransitioning(false);
              setIsReportTutorialRunning(false);
            }
            if (status === STATUS.SKIPPED) navigate(basePath, { replace: true });
          }}
        />
      )}
    </>
  );
}
